<script setup lang="ts">
import { app as appInfo } from '@shared/lib/app'
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import {
  ApiError,
  getCurrentAttempt,
  getTime,
  openEventStream,
  saveResponses,
  sendHeartbeat,
  sendIntegrity,
  submitAttempt,
} from '../api'
import {
  clearedResponse,
  isAnswered,
  itemTypeLabel,
  selectedBoolean,
  selectedOption,
  chosenOptions,
  toggledOptions,
  pairedColumn,
  pairedResponse,
  typedText,
  textResponse,
  singleChoiceResponse,
  trueFalseResponse,
} from '../lib/answers'
import {
  createEvent,
  draftStorageKey,
  loadDrafts,
  removeAcked,
  storeDrafts,
  upsertDraft,
  type PendingEvent,
} from '../lib/drafts'
import {
  createFocusMonitor,
  flushIntegrityEvents,
  integrityStorageKey,
  safeStorage,
  type FocusMonitor,
  type IntegrityEvent,
} from '../lib/focusMonitor'
import { createIntegrityReporter, type IntegrityReporter } from '../lib/integrityReport'
import { createHeartbeat, type Heartbeat } from '../lib/heartbeat'
import type { Phase } from '../lib/phase'
import { formatClock, formatCountdown, remainingMs, retryDelayJittered, serverOffset } from '../lib/time'
import { finishedStorageKey, firstSyncDelayMs, nextSyncDelayMs } from '../lib/offlineSync'
import { applyOrder, shuffleItemOrder, shuffleOptionOrder } from '../lib/shuffle'
import PhaseStatus from './PhaseStatus.vue'
import type { AttemptView, ItemView, Participant, TickPayload } from '../types'

const props = defineProps<{
  token: string
  participant: Participant
  initial: AttemptView
}>()

const emit = defineEmits<{
  submitted: [view: AttemptView]
  /** message is the server's explanation when there is one worth showing (another device took over). */
  unauthorized: [message?: string]
}>()

type SyncState = 'saved' | 'saving' | 'pending' | 'offline' | 'error'
type StreamState = 'connecting' | 'live' | 'polling'

const exam = computed(() => props.initial.exam)
// The seed comes from the server and is persisted there, so reloads and drafts
// never change the order. Answers stay keyed by item_id.
const shuffleSeed = props.initial.shuffle_seed ?? ''
const items = computed(() => {
  const base = [...props.initial.items].sort((a, b) => a.order - b.order)
  if (!shuffleSeed || !props.initial.exam.shuffle_items) return base
  return applyOrder(base, shuffleItemOrder(base.length, shuffleSeed))
})
const displayNumber = computed(() => {
  const numbers = new Map<string, number>()
  items.value.forEach((item, index) => numbers.set(item.id, index + 1))
  return numbers
})
const optionsShuffled = Boolean(shuffleSeed && props.initial.exam.shuffle_options)
// Options are shown in shuffled order, but the original key is what gets answered.
const currentOptions = computed(() => {
  const options = currentItem.value.presentation.options ?? []
  if (!optionsShuffled) return options
  return applyOrder(options, shuffleOptionOrder(options.length, shuffleSeed, currentItem.value.id))
})
function optionLabel(key: string, index: number): string {
  return optionsShuffled ? String.fromCharCode(65 + index) : key
}

const notices = ref<Record<string, string>>({ ...(props.initial.notices ?? {}) })
const currentNotice = computed(() => notices.value[currentItem.value.id] ?? '')
const codeOpen = ref(false)
const codeCopied = ref(false)
function toggleCode(): void {
  codeOpen.value = !codeOpen.value
  codeCopied.value = false
}
async function copyCode(): Promise<void> {
  try {
    await navigator.clipboard.writeText(currentItem.value.id)
    codeCopied.value = true
  } catch {
    codeCopied.value = false
  }
}
const storageKey = draftStorageKey(exam.value.id, props.initial.attempt.participant_id)
// Offline mode (set by the admin): every question is already here, answers are queued on this device and sent in one
// batch every syncSeconds (at a random moment), and a finish that cannot reach the server waits for it. The answer
// KEYS are never on the device; only what the participant typed is.
const offlineMode = Boolean(props.initial.exam.offline_mode)
const syncSeconds = props.initial.exam.sync_interval_seconds ?? 45
const finishedKey = finishedStorageKey(storageKey)
const finishedLocally = ref(false)

const responses = reactive<Record<string, unknown>>({})
for (const stored of props.initial.responses) {
  responses[stored.item_id] = stored.response
}

const drafts = ref<PendingEvent[]>([])
const currentIndex = ref(0)
const currentItem = computed<ItemView>(() => items.value[currentIndex.value])
watch(currentIndex, () => {
  codeOpen.value = false
})
const offset = ref(serverOffset(props.initial.server_time))
const deadline = ref(props.initial.deadline)
const remaining = ref(remainingMs(deadline.value, offset.value))
const syncState = ref<SyncState>('saved')
const streamState = ref<StreamState>('connecting')
const latestResponseAt = props.initial.responses.reduce<string | null>((latest, response) => {
  if (!response.answered_at) return latest
  if (!latest) return response.answered_at
  return Date.parse(response.answered_at) > Date.parse(latest) ? response.answered_at : latest
}, null)
const lastSavedAt = ref<Date | null>(latestResponseAt ? new Date(latestResponseAt) : null)
const online = ref(typeof navigator === 'undefined' ? true : navigator.onLine)
const submitting = ref(false)
const autoSubmitting = ref(false)
const submitError = ref('')
const submitQueued = ref(false)
const saveSlow = ref(false)
const saveRetryAt = ref<number | null>(null)
const saveRetryNo = ref(0)
const submitRetryAt = ref<number | null>(null)
const submitRetryNo = ref(0)
const savePhaseSince = ref(Date.now())
const submitPhaseSince = ref(Date.now())
const fatalError = ref('')
const confirmOpen = ref(false)
const dialogRef = ref<HTMLDialogElement | null>(null)

const expired = ref(false)
const answeredCount = computed(() => items.value.filter((item) => isAnswered(responses[item.id])).length)
const unansweredItems = computed(() => items.value.filter((item) => !isAnswered(responses[item.id])))
const progressPercent = computed(() =>
  items.value.length === 0 ? 0 : Math.round((answeredCount.value / items.value.length) * 100),
)
// Phase shown to the participant (FR-C14). Quick saves stay quiet; the phase line only
// appears when a save has run for over a second, so the sidebar does not flicker.
const savePhase = computed<Phase | null>(() => {
  if (!online.value && drafts.value.length > 0) return 'offline'
  if (saveRetryAt.value !== null) return 'retrying'
  if (saveSlow.value) return 'saving'
  return null
})
const submitPhase = computed<Phase | null>(() => {
  if (submitting.value) return 'submitting'
  if (submitQueued.value) return 'waiting'
  if (submitRetryAt.value !== null) return 'retrying'
  return null
})
watch(savePhase, () => {
  savePhaseSince.value = Date.now()
})
watch(submitPhase, () => {
  submitPhaseSince.value = Date.now()
})
const canAnswer = computed(() => !expired.value && !submitting.value && !fatalError.value && !finishedLocally.value)
const timerLabel = computed(() => formatCountdown(remaining.value))
const timerTone = computed(() => {
  if (remaining.value <= 60_000) return 'danger'
  if (remaining.value <= 5 * 60_000) return 'warning'
  return 'normal'
})
const syncLabel = computed(() => {
  if (!online.value) return drafts.value.length > 0 ? 'Tidak terhubung — belum tersimpan' : 'Tidak terhubung'
  switch (syncState.value) {
    case 'saving':
      return 'Menyimpan…'
    case 'pending':
      return offlineMode ? 'Di perangkat — dikirim berkala' : 'Menunggu simpan'
    case 'offline':
      return 'Tidak terhubung — belum tersimpan'
    case 'error':
      return 'Gagal menyimpan'
    default:
      return lastSavedAt.value ? 'Tersimpan' : 'Belum ada jawaban'
  }
})
const syncTone = computed(() => {
  if (!online.value) return 'offline'
  if (syncState.value === 'error' || syncState.value === 'offline') return 'error'
  if (syncState.value === 'saved') return lastSavedAt.value ? 'saved' : 'neutral'
  return 'pending'
})
const lastSavedLabel = computed(() =>
  lastSavedAt.value ? `Tersimpan pukul ${formatClock(lastSavedAt.value)}` : 'Belum ada jawaban tersimpan',
)

let stopped = false
let saveInFlight = false
let saveAgain = false
let submitRequested = false
let retryCount = 0
let submitRetryCount = 0
let saveTimer: number | null = null
let retryTimer: number | null = null
let clockTimer: number | null = null
let autosaveTimer: number | null = null
let syncTimer: number | null = null
let noticeTimer: number | null = null
let stopStream: (() => void) | null = null
let slowSaveTimer: number | null = null
let offlineSyncTimer: number | null = null
let lastRemainingSecond = -1
let heartbeat: Heartbeat | null = null

// Focus monitoring is evidence only: it never blocks answering or submits for the participant.
const FOCUS_WARNING_LIMIT = 3
const integrityKey = integrityStorageKey(props.initial.exam.id, props.initial.attempt.id)
const integrityCount = ref(0)
const awayNotice = ref<IntegrityEvent | null>(null)
let focusMonitor: FocusMonitor | null = null
let integrityReporter: IntegrityReporter | null = null

function startFocusMonitor(): void {
  if (props.initial.attempt.status !== 'active') return
  integrityReporter = createIntegrityReporter({ send: (events) => sendIntegrity(props.token, events) })
  focusMonitor = createFocusMonitor({
    storageKey: integrityKey,
    getServerOffset: () => offset.value,
    onChange: (events) => {
      integrityCount.value = events.filter((event) => event.duration_ms !== null).length
      integrityReporter?.update(events)
    },
    onReturn: (event) => {
      awayNotice.value = event
      void integrityReporter?.flushNow()
      void nextTick(() => {
        const dlg = awayDialogRef.value
        if (dlg && !dlg.open && !stopped) {
          dlg.showModal()
          awayOkRef.value?.focus()
        }
      })
    },
  })
  focusMonitor.start()
}

function stopFocusMonitor(): void {
  if (!focusMonitor) return
  focusMonitor.stop()
  // Keep a local copy as well, then give the server a last chance to hear about open episodes.
  flushIntegrityEvents(safeStorage(), integrityKey, focusMonitor.getEvents())
  integrityReporter?.update(focusMonitor.getEvents())
  void integrityReporter?.flushNow().finally(() => integrityReporter?.stop())
  focusMonitor = null
}

const awayDialogRef = ref<HTMLDialogElement | null>(null)
const awayOkRef = ref<HTMLButtonElement | null>(null)
function acknowledgeAway(): void {
  awayDialogRef.value?.close()
}
const participantInitial = computed(() => (props.participant.display_name.trim()[0] ?? '?').toUpperCase())
const isLastItem = computed(() => currentIndex.value >= items.value.length - 1)

const awaySeconds = computed(() =>
  awayNotice.value?.duration_ms == null ? 0 : Math.max(1, Math.round(awayNotice.value.duration_ms / 1000)),
)

function clearSaveTimer(): void {
  if (saveTimer !== null) {
    window.clearTimeout(saveTimer)
    saveTimer = null
  }
}

function clearRetryTimer(): void {
  if (retryTimer !== null) {
    window.clearTimeout(retryTimer)
    retryTimer = null
  }
  saveRetryAt.value = null
  submitRetryAt.value = null
}

function setRemaining(ms: number): void {
  // Publish to the reactive ref only when the displayed second changes (or time is up).
  const second = Math.ceil(ms / 1000)
  if (second === lastRemainingSecond && ms > 0) return
  lastRemainingSecond = second
  remaining.value = ms
}

function clearSlowSaveTimer(): void {
  if (slowSaveTimer !== null) {
    window.clearTimeout(slowSaveTimer)
    slowSaveTimer = null
  }
  saveSlow.value = false
}

function handleUnauthorized(error?: unknown): void {
  if (stopped) return
  stopped = true
  cleanup()
  emit('unauthorized', error instanceof ApiError && error.code === 'device_replaced' ? error.message : undefined)
}

function clearFinishedFlag(): void {
  try {
    localStorage.removeItem(finishedKey)
  } catch {
    // Storage may be unavailable; the flag only helps after a reload.
  }
}

function scheduleOfflineSync(first = false): void {
  if (stopped) return
  const delay = first ? firstSyncDelayMs(syncSeconds) : nextSyncDelayMs(syncSeconds)
  offlineSyncTimer = window.setTimeout(() => {
    offlineSyncTimer = null
    if (!stopped && !expired.value && !finishedLocally.value && drafts.value.length > 0) void flush()
    scheduleOfflineSync()
  }, delay)
}

function onVisibility(): void {
  // Leaving the page is the moment a closed tab or a sleeping phone loses the queue: send it now.
  if (document.hidden && !stopped && drafts.value.length > 0 && !submitting.value) void flush()
}

function finish(next: AttemptView): void {
  drafts.value = []
  storeDrafts(storageKey, [])
  clearFinishedFlag()
  stopped = true
  cleanup()
  emit('submitted', next)
}

function refreshIfSubmitted(): void {
  void getCurrentAttempt(props.token)
    .then((next) => {
      if (next.attempt.status === 'submitted') finish(next)
    })
    .catch(() => {
      // Keep working locally; the next action will surface the real error.
    })
}

function markFatal(message: string): void {
  fatalError.value = message
  autoSubmitting.value = false
  syncState.value = 'error'
  clearSaveTimer()
  clearRetryTimer()
}

function answer(item: ItemView, response: unknown): void {
  if (!canAnswer.value) return
  responses[item.id] = response
  const event = createEvent(item.id, response)
  drafts.value = upsertDraft(drafts.value, event)
  storeDrafts(storageKey, drafts.value)
  syncState.value = 'pending'
  submitError.value = ''
  if (!offlineMode) scheduleSave(700)
}

function clearAnswer(item: ItemView): void {
  answer(item, clearedResponse())
}

function goTo(index: number): void {
  if (index < 0 || index >= items.value.length) return
  currentIndex.value = index
}

function scheduleSave(delay: number): void {
  if (stopped || submitting.value) return
  clearSaveTimer()
  saveTimer = window.setTimeout(() => {
    saveTimer = null
    void flush()
  }, delay)
}

function scheduleRetry(retryAfterSeconds: number | null = null): void {
  if (stopped || submitting.value) return
  clearRetryTimer()
  retryCount += 1
  const delay = retryDelayJittered(retryCount, Math.random, retryAfterSeconds)
  saveRetryNo.value = retryCount
  saveRetryAt.value = Date.now() + delay
  retryTimer = window.setTimeout(() => {
    retryTimer = null
    saveRetryAt.value = null
    void flush()
  }, delay)
}

async function flush(): Promise<void> {
  if (stopped || submitting.value) return
  if (finishedLocally.value) {
    // The answers are locked and a finish is waiting for the server: that request carries the queue, so send it
    // instead of a plain save (a plain save would also cancel the pending retry of the finish).
    if (retryTimer === null) requestSubmit(true)
    return
  }
  if (saveInFlight) {
    saveAgain = true
    return
  }
  if (drafts.value.length === 0) {
    syncState.value = online.value ? 'saved' : 'offline'
    return
  }

  clearSaveTimer()
  clearRetryTimer()
  saveInFlight = true
  saveAgain = false
  syncState.value = 'saving'
  clearSlowSaveTimer()
  slowSaveTimer = window.setTimeout(() => {
    slowSaveTimer = null
    if (saveInFlight) saveSlow.value = true
  }, 1000)

  const snapshot = drafts.value.slice()
  const sentKeys = snapshot.map((event) => event.idempotency_key)
  try {
    const result = await saveResponses(props.token, snapshot)
    heartbeat?.touch()
    offset.value = serverOffset(result.server_time)
    deadline.value = result.deadline
    setRemaining(remainingMs(deadline.value, offset.value))
    drafts.value = removeAcked(drafts.value, sentKeys)
    storeDrafts(storageKey, drafts.value)
    lastSavedAt.value = new Date(result.server_time)
    retryCount = 0
    syncState.value = drafts.value.length > 0 ? 'pending' : 'saved'
    if (drafts.value.length > 0) saveAgain = true
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      handleUnauthorized(error)
      return
    }
    if (error instanceof ApiError && error.code === 'attempt_submitted') {
      refreshIfSubmitted()
      return
    }
    if (error instanceof ApiError && error.code === 'attempt_closed') {
      markFatal('Waktu ujian sudah berakhir dan attempt tidak dapat disimpan lagi.')
      return
    }
    if (error instanceof ApiError && error.code === 'invalid_response') {
      markFatal(error.message)
      return
    }
    syncState.value = online.value ? 'error' : 'offline'
    scheduleRetry(error instanceof ApiError ? error.retryAfterSeconds : null)
  } finally {
    saveInFlight = false
    clearSlowSaveTimer()
    if (!stopped && submitRequested) {
      submitRequested = false
      submitQueued.value = false
      void doSubmit()
    } else if (!stopped && saveAgain && drafts.value.length > 0) {
      saveAgain = false
      void flush()
    }
  }
}

function requestSubmit(fromTimer = false): void {
  if (stopped || submitting.value || fatalError.value) return
  autoSubmitting.value = fromTimer
  submitError.value = ''
  clearSaveTimer()
  if (saveInFlight) {
    submitRequested = true
    submitQueued.value = true
    return
  }
  void doSubmit()
}

async function doSubmit(): Promise<void> {
  if (stopped || submitting.value || fatalError.value) return
  submitting.value = true
  submitError.value = ''
  clearSaveTimer()
  clearRetryTimer()

  const snapshot = drafts.value.slice()
  try {
    const result = await submitAttempt(props.token, snapshot)
    finish(result)
  } catch (error) {
    submitting.value = false
    if (error instanceof ApiError && error.status === 401) {
      handleUnauthorized(error)
      return
    }
    if (error instanceof ApiError && error.code === 'attempt_submitted') {
      refreshIfSubmitted()
      return
    }
    if (error instanceof ApiError && error.code === 'attempt_closed') {
      markFatal('Waktu ujian sudah berakhir. Jawaban terakhir tidak dapat dikumpulkan.')
      return
    }
    if (error instanceof ApiError && error.code === 'invalid_response') {
      markFatal(error.message)
      return
    }
    if (offlineMode && error instanceof ApiError && (error.status === 0 || error.status >= 500)) {
      // The server cannot be reached: lock the answers on this device and keep trying until it can take them.
      finishedLocally.value = true
      autoSubmitting.value = true
      try {
        localStorage.setItem(finishedKey, '1')
      } catch {
        // Without storage a reload forgets the lock, but the answers are still in the draft queue.
      }
    }
    submitError.value =
      error instanceof ApiError ? error.message : 'Pengumpulan gagal. Periksa koneksi.'
    if (autoSubmitting.value) {
      clearRetryTimer()
      submitRetryCount += 1
      const delay = retryDelayJittered(
        submitRetryCount,
        Math.random,
        error instanceof ApiError ? error.retryAfterSeconds : null,
      )
      submitRetryNo.value = submitRetryCount
      submitRetryAt.value = Date.now() + delay
      retryTimer = window.setTimeout(() => {
        retryTimer = null
        submitRetryAt.value = null
        requestSubmit(true)
      }, delay)
    }
  }
}

function openConfirm(): void {
  if (!canAnswer.value || submitting.value || confirmOpen.value) return
  confirmOpen.value = true
  void nextTick(() => dialogRef.value?.showModal())
}

function closeConfirm(): void {
  dialogRef.value?.close()
}

function confirmSubmit(): void {
  dialogRef.value?.close()
  requestSubmit(false)
}

function applyTick(tick: TickPayload): void {
  if (stopped) return
  heartbeat?.touch()
  offset.value = serverOffset(tick.server_time)
  deadline.value = tick.deadline
  setRemaining(remainingMs(deadline.value, offset.value))
  streamState.value = 'live'
  if (tick.status === 'submitted') refreshIfSubmitted()
}

function checkClock(): void {
  if (stopped) return
  const left = remainingMs(deadline.value, offset.value)
  setRemaining(left)
  if (left <= 0 && !expired.value && !fatalError.value) {
    expired.value = true
    requestSubmit(true)
  }
}

function startTicker(): void {
  clockTimer = window.setInterval(checkClock, 500)
  if (offlineMode) {
    scheduleOfflineSync(true)
  } else {
    autosaveTimer = window.setInterval(() => {
      if (!stopped && !expired.value && drafts.value.length > 0) void flush()
    }, Math.max(5, exam.value.autosave_interval_seconds) * 1000)
  }
  syncTimer = window.setInterval(() => {
    if (stopped || streamState.value === 'live') return
    void getTime(props.token)
      .then((result) => {
        if (stopped) return
        heartbeat?.touch()
        offset.value = serverOffset(result.server_time)
        deadline.value = result.deadline
        setRemaining(remainingMs(deadline.value, offset.value))
        if (result.status === 'submitted') refreshIfSubmitted()
      })
      .catch((error) => {
        if (error instanceof ApiError && error.status === 401) handleUnauthorized(error)
      })
  }, offlineMode ? 60_000 : 20_000)
  // Errata notices are not on the realtime stream, so poll the light time endpoint.
  noticeTimer = window.setInterval(() => {
    if (stopped) return
    void getTime(props.token)
      .then((result) => {
        if (!stopped) notices.value = { ...(result.notices ?? {}) }
      })
      .catch(() => {
        // Notices are advisory; the next poll retries.
      })
  }, offlineMode ? 60_000 : 30_000)
}

function onOnline(): void {
  online.value = true
  if (finishedLocally.value) {
    requestSubmit(true)
    return
  }
  if (drafts.value.length > 0) {
    syncState.value = 'pending'
    void flush()
  }
}

function onOffline(): void {
  online.value = false
  if (drafts.value.length > 0) syncState.value = 'offline'
}

function onBeforeUnload(event: BeforeUnloadEvent): void {
  if (drafts.value.length > 0 && !stopped) {
    event.preventDefault()
    event.returnValue = ''
  }
}

function startHeartbeat(): void {
  // Presence only: a ping fires when nothing else has reached the server for 15 s.
  heartbeat = createHeartbeat({
    now: () => Date.now(),
    send: (hidden) => sendHeartbeat(props.token, hidden),
    isHidden: () => typeof document !== 'undefined' && document.hidden,
    isActive: () => online.value && !stopped && !submitting.value,
    setInterval: (fn, ms) => window.setInterval(fn, ms),
    clearInterval: (handle) => window.clearInterval(handle as number),
  })
  heartbeat.start()
}

function cleanup(): void {
  clearSaveTimer()
  clearRetryTimer()
  clearSlowSaveTimer()
  heartbeat?.stop()
  heartbeat = null
  if (clockTimer !== null) window.clearInterval(clockTimer)
  if (autosaveTimer !== null) window.clearInterval(autosaveTimer)
  if (offlineSyncTimer !== null) window.clearTimeout(offlineSyncTimer)
  offlineSyncTimer = null
  document.removeEventListener('visibilitychange', onVisibility)
  if (syncTimer !== null) window.clearInterval(syncTimer)
  if (noticeTimer !== null) window.clearInterval(noticeTimer)
  noticeTimer = null
  clockTimer = null
  autosaveTimer = null
  syncTimer = null
  stopStream?.()
  stopStream = null
  stopFocusMonitor()
  window.removeEventListener('online', onOnline)
  window.removeEventListener('offline', onOffline)
  window.removeEventListener('beforeunload', onBeforeUnload)
}

onMounted(() => {
  const restored = loadDrafts(storageKey)
  if (restored.length > 0) {
    for (const event of restored) responses[event.item_id] = event.response
    drafts.value = restored
    syncState.value = 'pending'
    lastSavedAt.value = null
  }
  if (remaining.value <= 0 && !fatalError.value) {
    expired.value = true
    requestSubmit(true)
  }
  if (offlineMode) {
    document.addEventListener('visibilitychange', onVisibility)
    let wasFinished = false
    try {
      wasFinished = localStorage.getItem(finishedKey) === '1'
    } catch {
      wasFinished = false
    }
    if (wasFinished) {
      finishedLocally.value = true
      requestSubmit(true)
    }
  }
  startTicker()
  startHeartbeat()
  startFocusMonitor()
  window.addEventListener('online', onOnline)
  window.addEventListener('offline', onOffline)
  window.addEventListener('beforeunload', onBeforeUnload)

  // The realtime stream is one open connection per participant; offline mode replaces it with a slow poll of the time endpoint.
  if (offlineMode) {
    streamState.value = 'polling'
  } else stopStream = openEventStream(props.token, {
    onTick: applyTick,
    onError: () => {
      if (!stopped) streamState.value = 'polling'
    },
    onUnauthorized: handleUnauthorized,
  })
})

onBeforeUnmount(cleanup)
</script>

<template>
  <div class="exam-shell">
    <header class="exam-header">
      <div class="brand">
        <span class="brand-mark" aria-hidden="true">OAP</span>
        <span class="brand-text">
          <strong>{{ appInfo.name }}</strong>
          <small>{{ exam.id }} · v{{ exam.version }}</small>
        </span>
      </div>

      <div class="exam-heading">
        <p class="eyebrow">Ujian berlangsung</p>
        <h1>{{ exam.title }}</h1>
      </div>

      <div class="exam-header__meta">
        <div class="timer" :class="`timer--${timerTone}`" role="timer" :aria-label="`Sisa waktu ${timerLabel}`">
          <span class="timer__label">Sisa waktu</span>
          <strong class="timer__value">{{ timerLabel }}</strong>
        </div>
        <button
          class="button button--primary header-submit"
          type="button"
          :disabled="submitting || submitQueued || Boolean(fatalError)"
          @click="openConfirm"
        >
          {{ submitting || submitQueued ? 'Mengirim…' : 'Selesai & kumpulkan' }}
        </button>
      </div>

      <div class="participant-chip" aria-label="Identitas peserta">
        <span class="participant-chip__avatar" aria-hidden="true">{{ participantInitial }}</span>
        <span class="participant-chip__body">
          <span class="participant-chip__name">{{ participant.display_name }}</span>
          <span class="participant-chip__id">
            <template v-if="participant.login_id">No. peserta {{ participant.login_id }}</template>
            <template v-else>ID {{ participant.id }}</template>
            <template v-if="participant.room"> · {{ participant.room }}</template>
          </span>
        </span>
      </div>
    </header>

    <div v-if="expired && !fatalError" class="timeup-banner" role="alert">
      <div>
        <strong>Waktu ujian habis.</strong>
        <span v-if="autoSubmitting">
          {{
            finishedLocally
              ? 'Jawaban sudah dikunci di perangkat ini. Menunggu server untuk dikumpulkan; jangan tutup halaman ini.'
              : submitError
                ? 'Pengumpulan gagal, mencoba lagi…'
                : 'Jawaban sedang dikumpulkan…'
          }}
        </span>
        <span v-else>Jawaban Anda telah dikirim.</span>
      </div>
      <span v-if="streamState === 'polling'" class="stream-note">Mode polling</span>
    </div>

    <p v-if="!online" class="banner banner--warning" role="status">
      Koneksi terputus. Jawaban baru tersimpan di perangkat ini sampai server dapat dihubungi.
    </p>
    <p v-if="fatalError" class="banner banner--error" role="alert">{{ fatalError }}</p>
    <p v-if="submitError" class="banner banner--error" role="alert">
      {{ submitError }}
      <button v-if="!expired" class="link-button" type="button" @click="requestSubmit(false)">
        Coba kumpulkan lagi
      </button>
    </p>


    <main id="main-content" class="exam-main">
      <section class="question-panel" aria-labelledby="question-stem">
        <div class="question-meta">
          <span class="question-index">
            Soal <strong>{{ currentIndex + 1 }}</strong> <span>/ {{ items.length }}</span>
          </span>
          <button
            class="code-toggle"
            type="button"
            :aria-expanded="codeOpen"
            aria-label="Kode soal"
            title="Kode soal"
            @click="toggleCode"
          >
            #
          </button>
          <span class="chip">{{ itemTypeLabel(currentItem) }}</span>
          <span class="chip chip--quiet">{{ currentItem.points }} poin</span>
        </div>

        <p v-if="codeOpen" class="code-panel" role="status">
          Kode soal: <code>{{ currentItem.id }}</code>
          <button class="link-button" type="button" @click="copyCode">{{ codeCopied ? 'Tersalin' : 'Salin' }}</button>
        </p>
        <p v-if="currentNotice" class="banner banner--warning" role="status">{{ currentNotice }}</p>

        <h2 id="question-stem" class="question-stem">{{ currentItem.presentation.stem }}</h2>

        <fieldset v-if="currentItem.type === 'single_choice'" class="options" :disabled="!canAnswer">
          <legend class="sr-only">Pilih satu jawaban</legend>
          <label
            v-for="(option, optionIndex) in currentOptions"
            :key="option.key"
            class="option"
            :class="{ 'option--selected': selectedOption(responses[currentItem.id]) === option.key }"
          >
            <input
              type="radio"
              :name="`item-${currentItem.id}`"
              :value="option.key"
              :checked="selectedOption(responses[currentItem.id]) === option.key"
              @change="answer(currentItem, singleChoiceResponse(option.key))"
            />
            <span class="option__key" aria-hidden="true">{{ optionLabel(option.key, optionIndex) }}</span>
            <span class="option__text">{{ option.text }}</span>
          </label>
        </fieldset>

        <fieldset v-else-if="currentItem.type === 'multiple_choice'" class="options" :disabled="!canAnswer">
          <legend class="sr-only">Pilih semua jawaban yang benar</legend>
          <p class="hint">Pilih semua jawaban yang benar.</p>
          <label
            v-for="(option, optionIndex) in currentItem.presentation.options ?? []"
            :key="option.key"
            class="option"
            :class="{ 'option--selected': chosenOptions(responses[currentItem.id]).includes(option.key) }"
          >
            <input
              type="checkbox"
              :checked="chosenOptions(responses[currentItem.id]).includes(option.key)"
              @change="answer(currentItem, toggledOptions(responses[currentItem.id], option.key))"
            />
            <span class="option__key" aria-hidden="true">{{ String.fromCharCode(65 + optionIndex) }}</span>
            <span class="option__text">{{ option.text }}</span>
          </label>
        </fieldset>

        <fieldset v-else-if="currentItem.type === 'matching'" class="matching" :disabled="!canAnswer">
          <legend class="sr-only">Jodohkan setiap pernyataan</legend>
          <p class="hint">Pilih pasangan yang sesuai untuk setiap baris.</p>
          <ol class="matching__columns">
            <li v-for="column in currentItem.presentation.columns ?? []" :key="column.key">
              <strong>{{ column.key }}</strong> {{ column.text }}
            </li>
          </ol>
          <div v-for="row in currentItem.presentation.rows ?? []" :key="row.key" class="matching__row">
            <label :for="`pair-${currentItem.id}-${row.key}`" class="matching__label">{{ row.text }}</label>
            <select
              :id="`pair-${currentItem.id}-${row.key}`"
              class="matching__select"
              :value="pairedColumn(responses[currentItem.id], row.key)"
              @change="answer(currentItem, pairedResponse(responses[currentItem.id], row.key, ($event.target as HTMLSelectElement).value))"
            >
              <option value="">— pilih —</option>
              <option v-for="column in currentItem.presentation.columns ?? []" :key="column.key" :value="column.key">
                {{ column.key }}
              </option>
            </select>
          </div>
        </fieldset>

        <div v-else-if="currentItem.type === 'short_answer'" class="text-answer">
          <label class="sr-only" :for="`text-${currentItem.id}`">Jawaban singkat</label>
          <input
            :id="`text-${currentItem.id}`"
            class="text-answer__input"
            type="text"
            maxlength="500"
            autocomplete="off"
            placeholder="Ketik jawaban Anda"
            :disabled="!canAnswer"
            :value="typedText(responses[currentItem.id])"
            @input="answer(currentItem, textResponse(($event.target as HTMLInputElement).value))"
          />
        </div>

        <div v-else-if="currentItem.type === 'essay'" class="text-answer">
          <label class="sr-only" :for="`text-${currentItem.id}`">Jawaban uraian</label>
          <textarea
            :id="`text-${currentItem.id}`"
            class="text-answer__input text-answer__area"
            rows="8"
            maxlength="20000"
            placeholder="Tulis jawaban Anda"
            :disabled="!canAnswer"
            :value="typedText(responses[currentItem.id])"
            @input="answer(currentItem, textResponse(($event.target as HTMLTextAreaElement).value))"
          />
        </div>

        <fieldset v-else class="options options--boolean" :disabled="!canAnswer">
          <legend class="sr-only">Pilih Benar atau Salah</legend>
          <label
            class="option"
            :class="{ 'option--selected': selectedBoolean(responses[currentItem.id]) === true }"
          >
            <input
              type="radio"
              :name="`item-${currentItem.id}`"
              :checked="selectedBoolean(responses[currentItem.id]) === true"
              @change="answer(currentItem, trueFalseResponse(true))"
            />
            <span class="option__key option__key--word" aria-hidden="true">B</span>
            <span class="option__text">Benar</span>
          </label>
          <label
            class="option"
            :class="{ 'option--selected': selectedBoolean(responses[currentItem.id]) === false }"
          >
            <input
              type="radio"
              :name="`item-${currentItem.id}`"
              :checked="selectedBoolean(responses[currentItem.id]) === false"
              @change="answer(currentItem, trueFalseResponse(false))"
            />
            <span class="option__key option__key--word" aria-hidden="true">S</span>
            <span class="option__text">Salah</span>
          </label>
        </fieldset>

        <div class="question-actions">
          <button
            class="button button--ghost"
            type="button"
            :disabled="!canAnswer || !isAnswered(responses[currentItem.id])"
            @click="clearAnswer(currentItem)"
          >
            Kosongkan jawaban
          </button>
          <div class="question-actions__nav">
            <button
              class="button button--secondary"
              type="button"
              :disabled="currentIndex === 0"
              @click="goTo(currentIndex - 1)"
            >
              Sebelumnya
            </button>
            <button
              v-if="!isLastItem"
              class="button button--primary"
              type="button"
              @click="goTo(currentIndex + 1)"
            >
              Berikutnya
            </button>
            <button
              v-else
              class="button button--primary"
              type="button"
              :disabled="submitting || submitQueued || Boolean(fatalError)"
              @click="openConfirm"
            >
              Selesai & kumpulkan
            </button>
          </div>
        </div>
      </section>

      <aside class="exam-side" aria-label="Ringkasan dan navigasi ujian">
        <section class="side-card">
          <div class="side-card__head">
            <h2>Navigasi soal</h2>
            <span class="stream-badge" :class="`stream-badge--${streamState}`">
              {{ streamState === 'live' ? 'Realtime' : streamState === 'polling' ? 'Polling' : 'Menyambung' }}
            </span>
          </div>

          <div class="progress-block">
            <div class="progress-copy">
              <span><strong>{{ answeredCount }}</strong> dari {{ items.length }} terjawab</span>
              <span>{{ progressPercent }}%</span>
            </div>
            <div class="progress-track" role="progressbar" :aria-valuenow="answeredCount" aria-valuemin="0" :aria-valuemax="items.length">
              <span :style="{ width: `${progressPercent}%` }" />
            </div>
          </div>

          <nav class="nav-grid" aria-label="Pilih nomor soal">
            <button
              v-for="(item, index) in items"
              :key="item.id"
              class="nav-cell"
              :class="{
                'nav-cell--answered': isAnswered(responses[item.id]),
                'nav-cell--current': index === currentIndex,
              }"
              type="button"
              :aria-label="`Soal ${index + 1}${isAnswered(responses[item.id]) ? ', sudah dijawab' : ', belum dijawab'}`"
              :aria-current="index === currentIndex ? 'true' : undefined"
              @click="goTo(index)"
            >
              {{ index + 1 }}
            </button>
          </nav>

          <ul class="legend" aria-label="Keterangan navigasi">
            <li><span class="legend__dot legend__dot--answered" />Terjawab</li>
            <li><span class="legend__dot legend__dot--current" />Soal aktif</li>
            <li><span class="legend__dot" />Belum dijawab</li>
          </ul>

          <button
            class="button button--primary button--wide"
            type="button"
            :disabled="submitting || submitQueued || Boolean(fatalError)"
            @click="openConfirm"
          >
            {{ submitting || submitQueued ? 'Mengirim jawaban…' : 'Kumpulkan jawaban' }}
          </button>
          <PhaseStatus
            :phase="submitPhase"
            :since="submitPhaseSince"
            :attempt="submitRetryNo"
            :retry-at="submitRetryAt"
          />
          <p class="side-note">
            Waktu tersisa {{ timerLabel }}. Pengumpulan tetap dapat dilakukan hingga
            {{ exam.grace_seconds }} detik setelah waktu habis.
          </p>
        </section>

        <section class="side-card side-card--sync" aria-label="Status penyimpanan">
          <div class="sync-row" aria-live="polite">
            <span class="status-dot" :class="`status-dot--${syncTone}`" aria-hidden="true" />
            <div>
              <strong>{{ syncLabel }}</strong>
              <p>{{ lastSavedLabel }}</p>
            </div>
          </div>
          <PhaseStatus
            :phase="savePhase"
            :since="savePhaseSince"
            :attempt="saveRetryNo"
            :retry-at="saveRetryAt"
            reserve
          />
          <p v-if="drafts.length > 0" class="side-note side-note--warning">
            {{ drafts.length }} jawaban belum dikonfirmasi server.
          </p>
          <button
            class="button button--secondary button--wide"
            type="button"
            :disabled="syncState === 'saving' || drafts.length === 0 || submitting"
            @click="flush"
          >
            {{ syncState === 'saving' ? 'Menyimpan…' : 'Simpan sekarang' }}
          </button>
          <p class="side-note">
            Indikator <strong>Tersimpan</strong> berarti server sudah menerima jawaban, bukan
            sekadar menyimpannya di perangkat.
          </p>
        </section>
      </aside>
    </main>

    <dialog
      ref="awayDialogRef"
      class="confirm-dialog away-dialog"
      aria-labelledby="away-title"
      @cancel.prevent
      @close="awayNotice = null"
    >
      <div v-if="awayNotice">
        <p class="eyebrow eyebrow--danger">Peringatan</p>
        <h2 id="away-title">Anda meninggalkan halaman ujian</h2>
        <p>
          Anda berada di luar halaman ujian selama <strong>{{ awaySeconds }} detik</strong>. Kejadian ini
          tercatat dan dapat ditinjau pengawas. Jawaban Anda tetap aman dan waktu ujian tetap berjalan.
        </p>
        <p class="away-dialog__count">
          Kejadian tercatat: <strong>{{ integrityCount }}</strong>
          <span v-if="integrityCount >= FOCUS_WARNING_LIMIT">
            — sudah mencapai batas tinjauan. Tetaplah di halaman ujian.
          </span>
        </p>
        <div class="dialog-actions">
          <button ref="awayOkRef" class="button button--primary" type="button" @click="acknowledgeAway">
            Saya mengerti, kembali ke ujian
          </button>
        </div>
      </div>
    </dialog>

    <dialog ref="dialogRef" class="confirm-dialog" aria-labelledby="confirm-title" @close="confirmOpen = false">
      <div v-if="confirmOpen">
        <p class="eyebrow">Konfirmasi akhir</p>
        <h2 id="confirm-title">Kumpulkan jawaban sekarang?</h2>
        <p>
          Setelah dikumpulkan, jawaban tidak dapat diubah lagi. Pastikan seluruh soal telah
          diperiksa.
        </p>
        <p v-if="unansweredItems.length > 0" class="banner banner--warning">
          Masih ada <strong>{{ unansweredItems.length }} soal</strong> belum dijawab:
          {{
            unansweredItems
              .slice(0, 10)
              .map((item) => displayNumber.get(item.id))
              .join(', ')
          }}{{ unansweredItems.length > 10 ? ', …' : '' }}
        </p>
        <p v-else class="banner banner--success">Semua {{ items.length }} soal sudah dijawab.</p>
        <dl class="confirm-facts">
          <div><dt>Peserta</dt><dd>{{ participant.display_name }}</dd></div>
          <div><dt>Terjawab</dt><dd>{{ answeredCount }} / {{ items.length }}</dd></div>
          <div><dt>Belum tersimpan</dt><dd>{{ drafts.length }} jawaban</dd></div>
        </dl>
        <div class="dialog-actions">
          <button class="button button--secondary" type="button" @click="closeConfirm">
            Periksa lagi
          </button>
          <button class="button button--primary" type="button" :disabled="submitting" @click="confirmSubmit">
            {{ submitting ? 'Mengumpulkan…' : 'Ya, kumpulkan' }}
          </button>
        </div>
      </div>
    </dialog>
  </div>
</template>
